import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { LoadingSpinner } from "widgets";

import type { Group, InputTableQuerySpec, Organism, Playbook, VariantAnalysisGroup } from "../../../graphql.js";
import { type IAppState } from "../../../redux/reducer.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import { DATATABLE_ID_TYPE, ENTITY_TYPE, ORGANISM_TABLE_COLUMNS, PLAYBOOK_CLASS } from "../../../settings/settings.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import { type ISelectionModel, createSelectionModel } from "@/app-utils/selection-model.ts";
import { getRelatedAnalysisGroups } from "@/redux/modules/genome-browser.ts";
import { submitNfPlaybook, updateNfPlaybookExecutionState } from "@/redux/modules/nf-playbook-execution.ts";
import { shouldLoadGroup } from "@/redux/modules/saved-group.ts";
import { createTableSpecForAnalysisGroupParent, createUniqueKeyFromTableSpec } from "@/redux/modules/table-data.ts";
import { type ITableSpecWithContext } from "@/ui-modules/content-browser/ContentBrowserTableView.tsx";
import ColumnSelectionButton from "@/ui-modules/core/ColumnSelectionButton.tsx";
import DataTableContainer from "@/ui-modules/core/grid/DataTableContainer.tsx";
import PageHeader from "@/ui-modules/core/navigation-panel/PageHeader.tsx";

type INfPlaybookBasicsScreenProps = IPlaybookWizardStepProvidedProps & IPlaybookWizardStepInjectedProps;

const NfGenomeSelectionScreen: FC<INfPlaybookBasicsScreenProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const reportTableQuerySpec = useSelector<IAppState, InputTableQuerySpec>(
    (state) => state.nfPlaybookExecution.reportTableQuerySpec,
  );
  const playbook = useSelector<IAppState, Playbook>((state) => state.nfPlaybookExecution.playbook);
  const [isInitializing, setInitializing] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const [availableAnalysisGroups, setAvailableAnalysisGroups] = useState<VariantAnalysisGroup[]>([]);

  const [selectedMutantGenomeIds, setSelectedMutantGenomeIds] = useState<number[]>([]);
  const onVcfMutantsSelectionModelChanged = useCallback((selection: ISelectionModel) => {
    setSelectedMutantGenomeIds(selection.selectedIds);
  }, []);

  // initialization to fetch possible analysis groups for the selection of mutant genomes, depending where we open the report from
  useEffect(() => {
    if (reportTableQuerySpec.queryType === ENTITY_TYPE.ORGANISM) {
      // VCF variants case
      void dispatch(getRelatedAnalysisGroups(reportTableQuerySpec.id)).then(
        (analysisGroups: VariantAnalysisGroup[]) => {
          setAvailableAnalysisGroups(analysisGroups);
        },
      );
    } else if (reportTableQuerySpec.queryType === ENTITY_TYPE.COORD_LIST) {
      void dispatch(getRelatedAnalysisGroups(reportTableQuerySpec.restrictions.refGenomeId)).then(
        (analysisGroups: VariantAnalysisGroup[]) => {
          if (!analysisGroups.length) {
            props.updateWizardMessage(
              `The reference genome for the selected coordinates does not belong to an analysis group.`,
            );
          }
          setAvailableAnalysisGroups(analysisGroups);
        },
      );
    } else if (reportTableQuerySpec.idType === DATATABLE_ID_TYPE.GROUP) {
      void dispatch(shouldLoadGroup(reportTableQuerySpec.id)).then((group: Group) => {
        setAvailableAnalysisGroups([group.analysisGroup]);
      });
    } else {
      props.updateWizardMessage("Your selection is not supported for the mutant genomes selection.");
      console.error(
        "The following table query specification is not supported for selecting a list of mutant genomes",
        JSON.stringify(reportTableQuerySpec),
      );
      setInitializing(false);
    }
  }, []);

  // after the possible analysis groups have been fetched, we can initialize the preselections
  useEffect(() => {
    if (availableAnalysisGroups?.length) {
      if (reportTableQuerySpec.restrictions?.variantGenomeIds) {
        setSelectedMutantGenomeIds(reportTableQuerySpec.restrictions?.variantGenomeIds);
      }
      setInitializing(false);
    }
  }, [availableAnalysisGroups]);

  // update the table query spec for report Playbook submission
  const handleSubmitData = useCallback(() => {
    const updatedReportTableQuerySpec: InputTableQuerySpec = {
      ...reportTableQuerySpec,
      restrictions: {
        ...reportTableQuerySpec.restrictions,
        variantGenomeIds: selectedMutantGenomeIds,
      },
    };
    dispatch(
      updateNfPlaybookExecutionState({
        reportTableQuerySpec: updatedReportTableQuerySpec,
      }),
    );
    if (playbook?.type.class === PLAYBOOK_CLASS.ALIGNMENT) {
      return dispatch(submitNfPlaybook())
        .then(() => {
          setIsSubmitting(false);
          props.proceedWithNextStep();
        })
        .catch((e) => {
          setErrorMessage(e.message);
          setIsSubmitting(false);
        });
    } else {
      props.proceedWithNextStep();
    }
  }, [selectedMutantGenomeIds]);

  useEffect(() => {
    if (!isInitializing) {
      if (selectedMutantGenomeIds?.length > 400) {
        props.updateWizardMessage("You cannot select more than 400 genomes");
      } else {
        props.updateWizardMessage(null);
      }
    }
  }, [selectedMutantGenomeIds, isInitializing]);

  // switching the vcf ref library/analysis group will lead to a new grid specification for genomes selection
  const vcfMutantGenomesTableSpec: ITableSpecWithContext = useMemo(() => {
    return {
      ...createTableSpecForAnalysisGroupParent(availableAnalysisGroups?.[0]),
      context: "playbook-screen", // because get coexist with the genome viewer's mutant genomes listing
    };
  }, [availableAnalysisGroups]);

  return (
    <PlaybookWizardStep
      {...props}
      playbookPage={{
        ...props.playbookPage,
      }}
      wizardFooterData={{
        error: errorMessage,
        nextButtonProps: {
          label: playbook?.type.class === PLAYBOOK_CLASS.ALIGNMENT ? "Submit" : "Next",
          isLoading: !!isInitializing || isSubmitting,
          onPress: handleSubmitData,
        },
      }}
    >
      <PageHeader
        newStyle={true}
        actionWidgets={[
          <ColumnSelectionButton
            portalContext={"playbook-report-context"}
            key={"variants-track-customization-dialog-action-1"}
          />,
        ]}
      />
      <div className={"with-filter-on-top"} style={{ backgroundColor: "white", height: "calc(100% - 50px)" }}>
        {isInitializing || !vcfMutantGenomesTableSpec ? (
          <LoadingSpinner />
        ) : (
          <DataTableContainer
            key={createUniqueKeyFromTableSpec(vcfMutantGenomesTableSpec)}
            tableSpec={vcfMutantGenomesTableSpec}
            tableInitializationOptions={{
              initialSelectionModel: createSelectionModel(selectedMutantGenomeIds),
              sortExpression: ORGANISM_TABLE_COLUMNS.NAME,
              //filterExpression: "isReference=='false'",
            }}
            portalContext={"playbook-report-context"}
            onSelectionModelChanged={onVcfMutantsSelectionModelChanged}
          />
        )}
      </div>
    </PlaybookWizardStep>
  );
};

export default NfGenomeSelectionScreen;
